import React, { createRef, useCallback } from 'react';
import * as Styles from './DnsCard.styles';
import {
  Alert,
  Button,
  IconDomain, IconFilledInfo, Tag, Tooltip,
} from 'gatorcomponents';
import { useDispatch, useSelector } from 'react-redux';
import { useFeatureIsOn, useFeatureValue } from '@growthbook/growthbook-react';
import { AI_AGENTS_DOMAINS_PAGE, AI_AGENTS_MERLIN_DOMAINS_CHAT_ID } from '@/config/GrowthBook/constants';
import { merlinActions } from '@/redux/modules';
import { setCookie } from '@/utils/Application/cookies';
import { PopoverContent } from '@/components/Popover/PopoverContent';
import { Popover } from '@/components/Popover';

const DnsCard = ({
  manageDomainHandlerLocale,
  isInPropagation,
  propagationStatus,
  detailState,
  isPointedToReserved,
  redirectToDnsWizard,
}) => {
  const guideRef = createRef(null);
  const { isIframeOpen } = useSelector(state => state.merlin);
  const chatId = useFeatureValue(AI_AGENTS_MERLIN_DOMAINS_CHAT_ID);
  const showAiAgentButton = useFeatureIsOn(AI_AGENTS_DOMAINS_PAGE);

  const dispatch = useDispatch();

  const handleRedirect = () => {
    redirectToDnsWizard && redirectToDnsWizard();
  };

  const getPopoverProps = useCallback(() => ({
    title: manageDomainHandlerLocale.cardOptions.dns.popover.title,
    description: manageDomainHandlerLocale.cardOptions.dns.popover.description,
    buttonLabel: manageDomainHandlerLocale.cardOptions.dns.popover.buttonLabel,
  }), [manageDomainHandlerLocale]);

  const popoverContent = <PopoverContent {...getPopoverProps()} />;

  const steps = [
    {
      popover: {
        key: 'domainsBannerButton',
        content: popoverContent,
        pointer: 'topRight',
        $maxWidth: 240,
      },
      mask: {
        customRadius: 100,
      },
    },
  ];

  const popoverStorageKey = 'notShowConfigureDomainPopover';
  const notShowBannerPopover = localStorage.getItem(popoverStorageKey) || isIframeOpen || !showAiAgentButton;

  const cookieName = 'merlinUrl';
  const openIframe = useCallback(() => {
    dispatch(merlinActions.setChatData({ cookieName, chatId, isIframeOpen: true }));
    setCookie(cookieName, true, 1);
  }, [chatId, dispatch]);

  return (
    <>
      {!notShowBannerPopover && <Popover steps={steps} guideRef={guideRef} storageKey={popoverStorageKey} />}
      <Styles.CardTitle>
        <IconDomain />
        {manageDomainHandlerLocale.cardOptions.dns.title}

        <Tooltip content={manageDomainHandlerLocale.cardOptions.dns.tooltip}>
          <IconFilledInfo color="primaryBlue" />
        </Tooltip>

        {isInPropagation && (
        <Styles.TagWrapperDesktop>
          <Tag
            variant="pending"
            label={manageDomainHandlerLocale.tags[propagationStatus.status]}
            testId={`${propagationStatus.status}-tag`}
          />
        </Styles.TagWrapperDesktop>
        )}
      </Styles.CardTitle>

      {isInPropagation && (
        <Styles.TagWrapperMobile>
          <Tag
            variant="pending"
            label={manageDomainHandlerLocale.tags[propagationStatus.status]}
            testId={`${propagationStatus.status}-tag`}
          />
        </Styles.TagWrapperMobile>
      )}

      <Styles.DnsContent>
        <>
          <Styles.DnsSpan data-testid="first-dns">
            {detailState.nameservers && detailState.nameservers.length
              ? `${detailState.nameservers[0]} `
              : '- '
          }
            <Styles.SlashSpan>/</Styles.SlashSpan>
          </Styles.DnsSpan>
          <Styles.DnsSpan data-testid="second-dns">
            {detailState.nameservers && detailState.nameservers.length
              ? `${detailState.nameservers[1]}`
              : '- '
          }
          </Styles.DnsSpan>
        </>
      </Styles.DnsContent>

      {isInPropagation && (
      <Styles.NoPaddingAlertWrapper>
        <Alert
          variant="info"
          withoutBackground
          description={manageDomainHandlerLocale.cardOptions.dns.alert[propagationStatus.status]}
          testId="in_progress-alert"
        />
      </Styles.NoPaddingAlertWrapper>
      )}

      {isPointedToReserved && (
      <Alert
        variant="warning"
        description={manageDomainHandlerLocale.cardOptions.dns.alert.reserved}
        testId="reserved-alert"
      />
      )}

      <Styles.ButtonsWrapper>
        <Button
          label={manageDomainHandlerLocale.cardOptions.dns.cta}
          onClick={handleRedirect}
          testId="dnsCta"
          variant="secondary"
          width="fit"
        />

        {showAiAgentButton && (
          <Styles.AiButtonWrapper ref={guideRef}>
            <Button
              label={manageDomainHandlerLocale.cardOptions.dns.useAiButtonLabel}
              onClick={openIframe}
              variant="tertiary"
              width="fit"
            />
          </Styles.AiButtonWrapper>
        )}
      </Styles.ButtonsWrapper>
    </>
  );
};

export default DnsCard;
